import { BreadcrumbJsonLd } from 'next-seo';
import PropTypes from 'prop-types';

import SchemaJsonLd from 'components/Seo/SchemaJsonLd';

import { getStoreImageUrl } from 'lib/image';
import {
  toAbsoluteLink,
  toStoreCouponsLink,
  toStoreOffersLink,
} from 'lib/links';
import placeholder from 'lib/placeholder';

import APP from 'constants/app';
import LINKS from 'constants/links';

const WebPageSchema = ({
  isOfferPage,
  serverDate,
  storeImage,
  storeName,
  storeSlug,
}) => {
  const pageUrl = isOfferPage
    ? toAbsoluteLink(toStoreOffersLink(storeSlug))
    : toAbsoluteLink(toStoreCouponsLink(storeSlug));
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: storeName,
    url: pageUrl,
    lastReviewed: serverDate,
  };

  if (storeImage && !storeImage.startsWith(APP.LOCAL_IMAGE_PATH)) {
    schema.primaryImageOfPage = {
      '@type': 'ImageObject',
      url: getStoreImageUrl(storeImage, 400),
    };
  }

  return <SchemaJsonLd id="webpage-schema" schema={schema} />;
};

WebPageSchema.propTypes = {
  isOfferPage: PropTypes.bool.isRequired,
  serverDate: PropTypes.string,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
};

const StoreJsonLd = ({
  contactSignals,
  isOfferPage,
  storeDescriptionText,
  storeDomain,
  storeImage,
  storeName,
  storeRating,
  storeRatingCount,
  storeSlug,
}) => {
  const fallbackUrl = isOfferPage
    ? toAbsoluteLink(toStoreOffersLink(storeSlug))
    : toAbsoluteLink(toStoreCouponsLink(storeSlug));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: storeName,
    url: storeDomain ? `https://${storeDomain}` : fallbackUrl,
  };

  if (storeImage && !storeImage.startsWith(APP.LOCAL_IMAGE_PATH)) {
    schema.image = getStoreImageUrl(storeImage, 400);
  }

  if (storeRating && storeRatingCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: storeRating,
      ratingCount: storeRatingCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  if (storeDescriptionText) {
    schema.description = storeDescriptionText;
  }

  if (contactSignals?.email || contactSignals?.customerServiceUrl) {
    const point = {
      '@type': 'ContactPoint',
      contactType: 'customer service',
    };
    if (contactSignals.email) point.email = contactSignals.email;
    if (contactSignals.customerServiceUrl) {
      point.url = contactSignals.customerServiceUrl;
    }
    schema.contactPoint = [point];
  }

  return <SchemaJsonLd id="store-schema" schema={schema} />;
};

StoreJsonLd.propTypes = {
  contactSignals: PropTypes.shape({
    customerServiceUrl: PropTypes.string,
    email: PropTypes.string,
    sameAs: PropTypes.arrayOf(PropTypes.string),
  }),
  isOfferPage: PropTypes.bool.isRequired,
  storeDescriptionText: PropTypes.string,
  storeDomain: PropTypes.string,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeRating: PropTypes.number,
  storeRatingCount: PropTypes.number,
  storeSlug: PropTypes.string.isRequired,
};

const OrganizationJsonLd = ({ isOfferPage, sameAs, storeName, storeSlug }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: storeName,
    url: isOfferPage
      ? toAbsoluteLink(toStoreOffersLink(storeSlug))
      : toAbsoluteLink(toStoreCouponsLink(storeSlug)),
    sameAs,
  };

  return <SchemaJsonLd id="organization-schema" schema={schema} />;
};

OrganizationJsonLd.propTypes = {
  isOfferPage: PropTypes.bool.isRequired,
  sameAs: PropTypes.arrayOf(PropTypes.string).isRequired,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
};

const StoreSchema = ({
  contactSignals = null,
  isOfferPage,
  serverDate,
  storeDescriptionText = null,
  storeDomain,
  storeImage,
  storeName,
  storeRating,
  storeRatingCount,
  storeSlug,
}) => (
  <>
    <BreadcrumbJsonLd
      itemListElements={[
        {
          position: 1,
          name: placeholder('breadcrumbs.app'),
          item: APP.URL,
        },
        {
          position: 2,
          name: isOfferPage
            ? placeholder('breadcrumbs.stores')
            : placeholder('breadcrumbs.coupons'),
          item: toAbsoluteLink(LINKS.STORES),
        },
        {
          position: 3,
          name: storeName,
          item: isOfferPage
            ? toAbsoluteLink(toStoreOffersLink(storeSlug))
            : toAbsoluteLink(toStoreCouponsLink(storeSlug)),
        },
      ]}
    />
    <WebPageSchema
      isOfferPage={isOfferPage}
      serverDate={serverDate}
      storeImage={storeImage}
      storeName={storeName}
      storeSlug={storeSlug}
    />
    <StoreJsonLd
      contactSignals={contactSignals}
      isOfferPage={isOfferPage}
      storeDescriptionText={storeDescriptionText}
      storeDomain={storeDomain}
      storeImage={storeImage}
      storeName={storeName}
      storeRating={storeRating}
      storeRatingCount={storeRatingCount}
      storeSlug={storeSlug}
    />
    {contactSignals?.sameAs?.length > 0 && (
      <OrganizationJsonLd
        isOfferPage={isOfferPage}
        sameAs={contactSignals.sameAs}
        storeName={storeName}
        storeSlug={storeSlug}
      />
    )}
  </>
);

StoreSchema.propTypes = {
  contactSignals: PropTypes.shape({
    customerServiceUrl: PropTypes.string,
    email: PropTypes.string,
    sameAs: PropTypes.arrayOf(PropTypes.string),
  }),
  isOfferPage: PropTypes.bool.isRequired,
  serverDate: PropTypes.string,
  storeDescriptionText: PropTypes.string,
  storeDomain: PropTypes.string,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeRating: PropTypes.number,
  storeRatingCount: PropTypes.number,
  storeSlug: PropTypes.string.isRequired,
};

export default StoreSchema;
